import React from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Circle } from 'lucide-react';
import { SortBy, SortOrderBy } from '../../../types';
import { useUserTags } from '../../../hooks/useTags';
import { cn } from '../../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

interface SortComponentProps {
  children: React.ReactNode;
}

interface SortOption {
  value: string;
  label: string;
}

const sortOptions: SortOption[] = [
  {
    value: 'createdAt-desc',
    label: 'Newest first',
  },
  {
    value: 'createdAt-asc',
    label: 'Oldest first',
  },
  {
    value: 'name-asc',
    label: 'Name (A → Z)',
  },
  {
    value: 'name-desc',
    label: 'Name (Z → A)',
  },
  {
    value: 'duration-desc',
    label: 'Longest first',
  },
  {
    value: 'duration-asc',
    label: 'Shortest first',
  },
];

const SortComponent: React.FC<SortComponentProps> = ({ children }) => {
  const { getTag, setTag } = useUserTags();
  const sortBy = (getTag('library-sort-by') as SortBy) || 'createdAt';
  const sortOrder = (getTag('library-sort-order') as SortOrderBy) || 'desc';

  const currentValue = `${sortBy}-${sortOrder}`;

  const handleSort = (value: string) => {
    const [type, order] = value.split('-') as [SortBy, SortOrderBy];
    setTag({ key: 'library-sort-by', value: type });
    setTag({ key: 'library-sort-order', value: order });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-48" align="end">
        <DropdownMenuPrimitive.RadioGroup
          value={currentValue}
          onValueChange={handleSort}
        >
          {sortOptions.map(({ value, label }) => (
            <DropdownMenuPrimitive.RadioItem
              key={value}
              value={value}
              className={cn(
                'relative flex cursor-default select-none items-center justify-between rounded-sm py-1.5 px-2 text-sm outline-none transition-colors',
                'focus:bg-accent focus:text-accent-foreground',
                'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              )}
            >
              <span>{label}</span>
              <DropdownMenuPrimitive.ItemIndicator>
                <Circle className="h-2 w-2 fill-primary text-primary" />
              </DropdownMenuPrimitive.ItemIndicator>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuPrimitive.RadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SortComponent;
